/**
 * Neureus Vector Database SDK Client
 *
 * Provides a simple interface to interact with the Neureus Vector Database,
 * supporting vector similarity search, hybrid search, index management,
 * and batch operations optimized for edge deployment.
 *
 * @example
 * ```typescript
 * import { VectorClient } from '@neureus/sdk/vector';
 *
 * const vectors = new VectorClient({
 *   apiKey: 'nru_...',
 *   baseUrl: 'https://api.neureus.ai'
 * });
 *
 * // Create an index
 * await vectors.indices.create({
 *   name: 'documents',
 *   dimension: 1536,
 *   metric: 'cosine'
 * });
 *
 * // Upsert vectors
 * await vectors.upsert({
 *   vectors: [
 *     {
 *       id: 'doc1',
 *       vector: [...], // 1536-dimensional embedding
 *       metadata: { title: 'Introduction', page: 1 }
 *     }
 *   ]
 * });
 *
 * // Search for similar vectors
 * const results = await vectors.search({
 *   vector: [...], // query embedding
 *   topK: 5,
 *   minSimilarity: 0.7
 * });
 * ```
 */

import ky, { type KyInstance } from 'ky';
import type {
  VectorEntry,
  VectorSearch,
  VectorSearchResponse,
  VectorIndexConfig,
  VectorIndexStats,
  HybridSearch,
} from '@neureus/vector-db';

// Re-export types from vector-db for convenience
export type {
  VectorEntry,
  VectorSearch,
  VectorSearchResponse,
  VectorSearchResult,
  VectorIndexConfig,
  VectorIndexStats,
  HybridSearch,
  SimilarityMetric,
  IndexType,
  VectorId,
  Vector,
  VectorDBError,
  VectorNotFoundError,
  IndexNotFoundError,
  DimensionMismatchError,
} from '@neureus/vector-db';

/**
 * Configuration options for VectorClient
 */
export interface VectorClientConfig {
  /**
   * Neureus API key (required)
   * Get your API key from https://app.neureus.ai/settings/api-keys
   */
  apiKey: string;

  /**
   * Base URL for the Neureus API
   * @default 'https://api.neureus.ai'
   */
  baseUrl?: string;

  /**
   * Request timeout in milliseconds
   * @default 30000 (30 seconds)
   */
  timeout?: number;

  /**
   * Number of retry attempts for failed requests
   * @default 3
   */
  retries?: number;

  /**
   * Default namespace for operations (optional)
   */
  namespace?: string;

  /**
   * Default index name for operations (optional)
   * @default 'default'
   */
  indexName?: string;
}

/**
 * Options for vector upsert operations
 */
export interface UpsertOptions {
  /**
   * The vector(s) to upsert
   */
  vectors: VectorEntry[];

  /**
   * Namespace to store vectors in (optional)
   */
  namespace?: string;

  /**
   * Index name (optional, defaults to 'default')
   */
  indexName?: string;
}

/**
 * Options for vector search operations
 */
export interface SearchOptions extends Omit<VectorSearch, 'vector'> {
  /**
   * Query vector
   */
  vector: number[];

  /**
   * Index name to search in (optional)
   */
  indexName?: string;
}

/**
 * Options for hybrid search operations
 */
export interface HybridSearchOptions extends Omit<HybridSearch, 'vector' | 'query'> {
  /**
   * Query vector (optional, one of vector or query required)
   */
  vector?: number[];

  /**
   * Text query (optional, one of vector or query required)
   */
  query?: string;

  /**
   * Index name to search in (optional)
   */
  indexName?: string;
}

/**
 * Main Vector client class
 */
export class VectorClient {
  private http: KyInstance;
  private config: Required<Omit<VectorClientConfig, 'namespace'>> & Pick<VectorClientConfig, 'namespace'>;

  constructor(config: VectorClientConfig) {
    this.config = {
      apiKey: config.apiKey,
      baseUrl: config.baseUrl || 'https://api.neureus.ai',
      timeout: config.timeout || 30000,
      retries: config.retries || 3,
      indexName: config.indexName || 'default',
      ...(config.namespace !== undefined && { namespace: config.namespace }),
    };

    this.http = ky.create({
      prefixUrl: this.config.baseUrl,
      timeout: this.config.timeout,
      retry: {
        limit: this.config.retries,
        methods: ['get', 'post', 'delete'],
        statusCodes: [408, 413, 429, 500, 502, 503, 504],
      },
      hooks: {
        beforeRequest: [
          (request) => {
            request.headers.set('Authorization', `Bearer ${this.config.apiKey}`);
            request.headers.set('Content-Type', 'application/json');
            request.headers.set('User-Agent', 'Neureus-SDK/0.2.0');
          },
        ],
      },
    });
  }

  /**
   * Index management API
   */
  public indices = {
    /**
     * Create a new vector index
     *
     * @example
     * ```typescript
     * await vectors.indices.create({
     *   name: 'documents',
     *   dimension: 1536, // OpenAI ada-002 dimensions
     *   metric: 'cosine',
     *   indexType: 'hnsw'
     * });
     * ```
     */
    create: async (config: VectorIndexConfig): Promise<{ success: boolean; indexName: string; message: string }> => {
      return this.http.post('vector/indices', {
        json: config,
      }).json();
    },

    /**
     * List all indices
     *
     * @example
     * ```typescript
     * const indices = await vectors.indices.list();
     * console.log(indices); // [{ name: 'documents', dimension: 1536, ... }]
     * ```
     */
    list: async (): Promise<{ indices: string[]; count: number }> => {
      return this.http.get('vector/indices').json();
    },

    /**
     * Get index statistics
     *
     * @example
     * ```typescript
     * const stats = await vectors.indices.stats('documents');
     * console.log(stats.vectorCount, stats.memoryUsage);
     * ```
     */
    stats: async (indexName: string): Promise<VectorIndexStats> => {
      return this.http.get(`vector/indices/${indexName}/stats`).json();
    },

    /**
     * Drop (delete) an index
     *
     * @example
     * ```typescript
     * await vectors.indices.drop('old-index');
     * ```
     */
    drop: async (indexName: string): Promise<{ success: boolean; message: string }> => {
      return this.http.delete(`vector/indices/${indexName}`).json();
    },
  };

  /**
   * Upsert (insert or update) a single vector
   *
   * @example
   * ```typescript
   * const id = await vectors.upsert({
   *   vectors: [{
   *     id: 'doc1',
   *     vector: [0.1, 0.2, ...], // embedding vector
   *     metadata: { title: 'Document 1', page: 1 }
   *   }]
   * });
   * ```
   */
  async upsert(options: UpsertOptions): Promise<{ success: boolean; ids: string[]; count: number; message: string }> {
    const { vectors, namespace, indexName } = options;

    // Use batch endpoint if multiple vectors
    if (vectors.length > 1) {
      return this.http.post('vector/vectors/batch', {
        json: {
          batch: {
            vectors,
            namespace: namespace || this.config.namespace,
          },
          indexName: indexName || this.config.indexName,
        },
      }).json();
    }

    // Single vector upsert
    const result = await this.http.post('vector/vectors', {
      json: {
        vector: vectors[0],
        namespace: namespace || this.config.namespace,
        indexName: indexName || this.config.indexName,
      },
    }).json<{ success: boolean; id: string; message: string }>();

    return {
      success: result.success,
      ids: [result.id],
      count: 1,
      message: result.message,
    };
  }

  /**
   * Get a vector by ID
   *
   * @example
   * ```typescript
   * const vector = await vectors.get('doc1');
   * console.log(vector.metadata, vector.vector);
   * ```
   */
  async get(id: string, namespace?: string): Promise<VectorEntry> {
    const params = new URLSearchParams();
    if (namespace || this.config.namespace) {
      params.set('namespace', namespace || this.config.namespace!);
    }

    return this.http.get(`vector/vectors/${id}`, { searchParams: params }).json();
  }

  /**
   * Delete a vector by ID
   *
   * @example
   * ```typescript
   * await vectors.delete('doc1');
   * ```
   */
  async delete(id: string, namespace?: string, indexName?: string): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams();
    if (namespace || this.config.namespace) {
      params.set('namespace', namespace || this.config.namespace!);
    }
    params.set('indexName', indexName || this.config.indexName);

    return this.http.delete(`vector/vectors/${id}`, { searchParams: params }).json();
  }

  /**
   * Search for similar vectors
   *
   * @example
   * ```typescript
   * const results = await vectors.search({
   *   vector: [0.1, 0.2, ...], // query embedding
   *   topK: 5,
   *   minSimilarity: 0.7,
   *   filter: { page: { $gt: 10 } }
   * });
   *
   * for (const result of results.matches) {
   *   console.log(result.id, result.score, result.metadata);
   * }
   * ```
   */
  async search(options: SearchOptions): Promise<VectorSearchResponse> {
    const { indexName, ...searchQuery } = options;

    return this.http.post('vector/search', {
      json: {
        query: {
          ...searchQuery,
          namespace: searchQuery.namespace || this.config.namespace,
        },
        indexName: indexName || this.config.indexName,
      },
    }).json();
  }

  /**
   * Hybrid search (vector + keyword)
   *
   * Combines vector similarity search with keyword/text search
   * for better retrieval accuracy.
   *
   * @example
   * ```typescript
   * const results = await vectors.hybridSearch({
   *   vector: [0.1, 0.2, ...],
   *   query: 'machine learning',
   *   topK: 10,
   *   alpha: 0.7 // 70% vector, 30% keyword
   * });
   * ```
   */
  async hybridSearch(options: HybridSearchOptions): Promise<VectorSearchResponse> {
    const { indexName, ...searchQuery } = options;

    return this.http.post('vector/search/hybrid', {
      json: {
        query: {
          ...searchQuery,
          namespace: searchQuery.namespace || this.config.namespace,
        },
        indexName: indexName || this.config.indexName,
      },
    }).json();
  }

  /**
   * Clear all vectors in a namespace
   *
   * @example
   * ```typescript
   * await vectors.clear('temporary-docs');
   * ```
   */
  async clear(namespace: string, indexName?: string): Promise<{ success: boolean; deletedCount: number; message: string }> {
    const params = new URLSearchParams();
    params.set('indexName', indexName || this.config.indexName);

    return this.http.delete(`vector/namespaces/${namespace}`, { searchParams: params }).json();
  }
}

/**
 * Create a Vector client instance
 *
 * @example
 * ```typescript
 * import { createVectorClient } from '@neureus/sdk/vector';
 *
 * const vectors = createVectorClient({
 *   apiKey: process.env.NEUREUS_API_KEY,
 *   indexName: 'my-docs'
 * });
 * ```
 */
export function createVectorClient(config: VectorClientConfig): VectorClient {
  return new VectorClient(config);
}
